/**
 * Vehicle Search page.
 *
 * Search a vehicle by registration, then see its fuel / oil / toll
 * touch-points over time — where it transacted, when, what it cost, and
 * whether the rule engine flagged it. Backs the workflow:
 *   GET /vehicles?q=<reg>            → pick a vehicle
 *   GET /vehicles/{reg}/timeline     → its touch-point visits
 */

import { useMemo, useState, type FormEvent } from 'react';
import { Coins, Droplet, Flag, Fuel, MapPin, Search } from 'lucide-react';

import EntityLocationMap, {
  type EntityLocationMarker,
} from '@/components/shared/EntityLocationMap';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useVehicleSearch, useVehicleTimeline } from '@/api/hooks/useVehicleTimeline';
import { formatAlertTime } from '@/lib/format';

function rand(cents: number): string {
  return (cents / 100).toLocaleString('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    maximumFractionDigits: 0,
  });
}

const KIND_STYLE: Record<string, { label: string; cls: string }> = {
  fuel: { label: 'Fuel', cls: 'bg-blue-500/15 text-blue-700 dark:text-blue-300' },
  oil: { label: 'Oil', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  toll: { label: 'Toll', cls: 'bg-violet-500/15 text-violet-700 dark:text-violet-300' },
};

export function VehicleSearch(): JSX.Element {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [selectedReg, setSelectedReg] = useState<string | null>(null);

  const searchQuery = useVehicleSearch(query);
  const timelineQuery = useVehicleTimeline(selectedReg ?? undefined, { limit: 200 });

  const results = searchQuery.data?.data ?? [];
  const timeline = timelineQuery.data;

  // Plot only the geocoded touch-points; historical rows often lack a location.
  const markers = useMemo<EntityLocationMarker[]>(
    () =>
      (timeline?.visits ?? [])
        .filter((v) => v.lat != null && v.lon != null)
        .map((v) => ({
          lat: v.lat as number,
          lon: v.lon as number,
          label: v.touchpoint_name ?? v.kind,
          count: 1,
        })),
    [timeline?.visits],
  );

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    setQuery(input.trim());
    setSelectedReg(null);
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Vehicle Search</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Find a vehicle by registration, then see its fuel &amp; oil touch-points over time.
        </p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={onSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Registration (e.g. ABC123GP)"
                maxLength={16}
                className="h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <Button type="submit" disabled={input.trim().length < 2}>
              Search
            </Button>
          </form>

          {query && (
            <div className="mt-4">
              {searchQuery.isLoading ? (
                <Skeleton className="h-24 w-full" />
              ) : searchQuery.isError ? (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                  Search failed: {(searchQuery.error as Error).message}
                </div>
              ) : results.length === 0 ? (
                <p className="text-sm text-muted-foreground">No vehicles match “{query}”.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Registration</TableHead>
                      <TableHead>Make / Model</TableHead>
                      <TableHead className="text-right">Tank</TableHead>
                      <TableHead className="text-right">L/100km</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.map((v) => (
                      <TableRow
                        key={v.asset_token}
                        className={
                          selectedReg === v.registration ? 'bg-muted/50' : 'cursor-pointer'
                        }
                        onClick={() => setSelectedReg(v.registration)}
                      >
                        <TableCell className="font-medium">{v.registration}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {[v.make, v.model].filter(Boolean).join(' ') || '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {v.tank_capacity_l != null ? `${v.tank_capacity_l} L` : '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {v.expected_l_per_100km != null
                            ? v.expected_l_per_100km.toFixed(1)
                            : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedReg(v.registration);
                            }}
                          >
                            Timeline →
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Timeline */}
      {selectedReg && (
        <Card>
          <CardHeader className="flex flex-col gap-2 pb-3 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <MapPin className="h-4 w-4" />
                Touch-point timeline — {selectedReg}
              </CardTitle>
              <p className="mt-1 text-xs text-muted-foreground">
                Most recent fuel, oil &amp; toll transactions, newest first.
              </p>
            </div>
            {timeline && (
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="text-xs">
                  <Coins className="mr-1 h-3 w-3" />
                  {rand(timeline.total_spend_cents)} total
                </Badge>
                <Badge variant="outline" className="text-xs">
                  <Fuel className="mr-1 h-3 w-3" />
                  {rand(timeline.fuel_spend_cents)} fuel
                </Badge>
                <Badge variant="outline" className="text-xs">
                  <Droplet className="mr-1 h-3 w-3" />
                  {rand(timeline.oil_spend_cents)} oil
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {rand(timeline.toll_spend_cents)} toll
                </Badge>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {timelineQuery.isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : timelineQuery.isError ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                Failed to load timeline: {(timelineQuery.error as Error).message}
              </div>
            ) : (
              <>
                {markers.length > 0 && <EntityLocationMap markers={markers} height="240px" />}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Touch-point</TableHead>
                      <TableHead className="text-right">Litres</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Odo (km)</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(timeline?.visits ?? []).map((v, i) => {
                      const k = KIND_STYLE[v.kind] ?? {
                        label: v.kind,
                        cls: 'bg-muted text-muted-foreground',
                      };
                      return (
                        <TableRow
                          key={`${v.transaction_time}-${i}`}
                          className={v.is_flagged ? 'bg-destructive/5' : undefined}
                        >
                          <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                            {formatAlertTime(v.transaction_time)}
                          </TableCell>
                          <TableCell>
                            <span
                              className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${k.cls}`}
                            >
                              {k.label}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm">
                            {v.touchpoint_name ?? (
                              <span className="italic text-muted-foreground">Unresolved</span>
                            )}
                            {v.brand ? (
                              <span className="ml-1 text-xs text-muted-foreground">· {v.brand}</span>
                            ) : null}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {v.litres != null ? v.litres.toFixed(1) : '—'}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {rand(v.amount_cents)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">
                            {v.odometer_km != null && v.odometer_km > 0
                              ? v.odometer_km.toLocaleString('en-ZA')
                              : '—'}
                          </TableCell>
                          <TableCell>
                            {v.is_flagged ? <Flag className="h-4 w-4 text-destructive" /> : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {timeline && timeline.visits.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                          No transactions for this vehicle.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default VehicleSearch;
